/**
 * Learn & Apply (R18, R19, R20).
 *
 * The screen opens with the GAP, not with a course list. Naming what changes —
 * task by task, in work language — is what lets someone judge a destination for
 * themselves, and it is the direct answer to the cohort's sharpest objection:
 * a "45% fit" for Product Manager cannot distinguish the ticket-moving version
 * of that job from the technically-deep one, but "you would be arbitrating
 * technical trade-offs" can, because the reader knows things about themselves
 * that no résumé encodes.
 *
 * Only then do resources appear, and every one of them states which gap skill
 * it closes. A list with no stated connection to this person's own gap is
 * advice, not a product, and it is indistinguishable from the listicles this is
 * meant to replace.
 *
 * Two columns, and the split is load-bearing:
 *   LEARN   AI Signals editions — change what you know
 *   APPLY   JTBD modules — change what you can show
 *
 * The right column matters more. A certificate nobody asked for is the clearest
 * case of a credential whose price has already collapsed: plentiful, verifiable,
 * worth nothing at the margin (ADR-021, ADR-034).
 */
import { useEffect, useState } from "react";
import { PANEL, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, GREEN, NAVY } from "../utils/constants";
import { computeGap, matchContent } from "../utils/gap";
import { recordSignal, EVENTS } from "../lib/signals";
import PulseLoader from "./PulseLoader";

const Label = ({ children, color = TEAL }) => (
  <div style={{ fontSize: 11, color, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase",
                fontFamily: "'JetBrains Mono', monospace", marginBottom: 8 }}>
    {children}
  </div>
);

function Closes({ skills }) {
  if (!skills?.length) return null;
  return (
    <div style={{ marginTop: 9, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "baseline" }}>
      <span style={{ fontSize: 10, color: CREAM30, fontFamily: "'JetBrains Mono', monospace" }}>
        CLOSES
      </span>
      {skills.map((s) => (
        <span key={s.id} style={{ fontSize: 11, color: TEAL, background: `${TEAL}12`,
                                  border: `1px solid ${TEAL}35`, borderRadius: 5, padding: "2px 7px" }}>
          {s.name}
        </span>
      ))}
    </div>
  );
}

function Card({ title, meta, body, url, badge, badgeColor, closes }) {
  const Tag = url ? "a" : "div";
  const props = url ? { href: url, target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <Tag {...props} style={{ display: "block", textDecoration: "none", background: PANEL,
                             border: `1px solid ${BORDER}`, borderRadius: 10, padding: 15, marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: url ? TEAL : CREAM, lineHeight: 1.4 }}>
          {title}{url ? " ↗" : ""}
        </div>
        {badge && (
          <span style={{ fontSize: 10, color: badgeColor, textTransform: "uppercase", letterSpacing: 1,
                         fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap" }}>
            {badge}
          </span>
        )}
      </div>
      {meta && <div style={{ fontSize: 11, color: CREAM30, marginTop: 3 }}>{meta}</div>}
      {body && <div style={{ fontSize: 12.5, color: CREAM60, marginTop: 7, lineHeight: 1.55 }}>{body}</div>}
      <Closes skills={closes} />
    </Tag>
  );
}

export default function Learn({ target, profile, currentCode, onBack }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      const [dwaMod, occMod, skillMod, contentMod] = await Promise.all([
        import("../data/taxonomy/dwas.json"),
        import("../data/taxonomy/occupations.json"),
        import("../data/taxonomy/skills.json"),
        import("../data/content.json"),
      ]);
      const occByCode = new Map(occMod.default.map((o) => [o.code, o]));

      // Skills this person has actually evidenced, so the gap is theirs rather
      // than the average holder's of their occupation code.
      const me = occByCode.get(currentCode);
      const evidencedSkillIds = new Set(
        Object.entries(me?.skills || {}).filter(([, v]) => v >= 3.2).map(([k]) => k)
      );

      const gap = computeGap({
        fromCode: currentCode,
        toCode: target?.code,
        dwaIndex: dwaMod.default,
        occByCode,
        skillDict: skillMod.default,
        confirmed: profile?.confirmedActivities || [],
        evidencedSkillIds,
        // An emerging role's defining skills are the reason it is on the list.
        // Without them the analysis runs entirely against the O*NET occupation
        // it borrows for activity data, and "Agent Operations Lead" quietly
        // becomes "IT Project Manager" the moment someone clicks it.
        emphasisSkillIds: target?.emerging ? target.definingSkills || [] : [],
      });

      const matched = matchContent(contentMod.default, gap, { persona: profile?.personaHint || null });
      if (!dead) {
        setState({ gap, ...matched });
        recordSignal(EVENTS.LEARN_OPENED, {
          occupationCode: currentCode, targetCode: target?.code,
        });
      }
    })();
    return () => { dead = true; };
  }, [target, profile, currentCode]);

  if (!state) return <PulseLoader text="Working out what actually changes…" />;
  const { gap, editions, modules, uncovered } = state;
  const name = target?.title || gap.targetTitle || "this role";

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <h2 style={{ color: CREAM, fontSize: 22, margin: "0 0 4px", fontFamily: "'Space Grotesk', sans-serif" }}>
        {gap.isRedesign ? `What changes if you rebuild your role as ${name}` : `What changes if you move to ${name}`}
      </h2>
      <p style={{ color: CREAM60, fontSize: 14, margin: "0 0 22px", maxWidth: "62ch" }}>
        {gap.isRedesign
          ? `${name} has no occupation of its own in the government data, so it is anchored to the ` +
            `one you are already in. That makes this a redesign rather than a move: the activity ` +
            `list would be comparing your job with itself, so it is left out. What is below is the ` +
            `skills half, which is the part that actually differs.`
          : "A percentage tells you nothing you can act on. Here is the actual work — what you " +
            "already do, and what would be new."}
      </p>

      {/* ── The gap, in work language ─────────────────────────────────── */}
      <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderTop: `2px solid ${TEAL}`,
                    borderRadius: 12, padding: 20, marginBottom: 22 }}>
        <div style={{ display: "flex", gap: 26, flexWrap: "wrap", marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, color: GREEN,
                          fontFamily: "'JetBrains Mono', monospace" }}>
              {gap.skillsCarried}<span style={{ color: CREAM30, fontSize: 18 }}>/{gap.skillsNeeded}</span>
            </div>
            <div style={{ fontSize: 11, color: CREAM60 }}>skills this role leans on that you already have</div>
          </div>
          {!gap.isRedesign && (
            <div>
              <div style={{ fontSize: 26, fontWeight: 700, color: AMBER,
                            fontFamily: "'JetBrains Mono', monospace" }}>{gap.newActivities.length}</div>
              <div style={{ fontSize: 11, color: CREAM60 }}>parts of the work that would be new</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 26, fontWeight: 700,
                          color: gap.missingSkills.length ? AMBER : GREEN,
                          fontFamily: "'JetBrains Mono', monospace" }}>{gap.missingSkills.length}</div>
            <div style={{ fontSize: 11, color: CREAM60 }}>skills you would need to build</div>
          </div>
        </div>

        {!gap.isRedesign && gap.newActivities.length === 0 && gap.missingSkills.length === 0 && (
          <p style={{ fontSize: 13, color: AMBER, lineHeight: 1.6, margin: "0 0 16px" }}>
            We cannot tell this destination apart from the work you already do — not because the
            move is easy, but because our data does not separate them. Treat that as a limit of
            this analysis rather than a finding about you.
          </p>
        )}

        {gap.newActivities.length > 0 && (
          <>
            <Label color={AMBER}>The new work</Label>
            <ul style={{ margin: "0 0 18px", padding: 0, listStyle: "none",
                         display: "flex", flexDirection: "column", gap: 7 }}>
              {gap.newActivities.slice(0, 8).map((a) => (
                <li key={a} style={{ display: "flex", gap: 9, alignItems: "flex-start",
                                     fontSize: 13.5, color: CREAM, lineHeight: 1.5 }}>
                  <span style={{ color: AMBER, fontFamily: "'JetBrains Mono', monospace",
                                 fontSize: 11, marginTop: 2 }}>+</span>
                  {a}
                </li>
              ))}
            </ul>
          </>
        )}

        {gap.carried.length > 0 && (
          <>
            <Label color={GREEN}>What you carry across</Label>
            <ul style={{ margin: "0 0 4px", padding: 0, listStyle: "none",
                         display: "flex", flexDirection: "column", gap: 6 }}>
              {gap.carried.slice(0, 5).map((a) => (
                <li key={a} style={{ display: "flex", gap: 9, alignItems: "flex-start",
                                     fontSize: 13, color: CREAM60, lineHeight: 1.5 }}>
                  <span style={{ color: GREEN, fontFamily: "'JetBrains Mono', monospace",
                                 fontSize: 11, marginTop: 2 }}>✓</span>
                  {a}
                </li>
              ))}
            </ul>
          </>
        )}

        {gap.skillsInferred && (
          <p style={{ fontSize: 11, color: CREAM30, margin: "14px 0 0", lineHeight: 1.55 }}>
            This occupation has no skill ratings in the government data — it is new enough that
            the survey has not reached it. The skill profile is inferred from closely related
            rated occupations, and is weaker evidence than the task list above.
          </p>
        )}
      </div>

      {/* ── Skills to close ───────────────────────────────────────────── */}
      {gap.missingSkills.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Label>What that adds up to</Label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {gap.missingSkills.slice(0, 8).map((s) => (
              <span key={s.id} style={{ background: "transparent", border: `1px dashed ${AMBER}55`,
                                        borderRadius: 8, padding: "7px 12px", fontSize: 13, color: CREAM }}>
                {s.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Routes, each stating what it closes ───────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
        <div>
          <Label>Ways to learn</Label>
          <p style={{ fontSize: 12, color: CREAM30, margin: "0 0 12px", lineHeight: 1.55 }}>
            Changes what you know. Free first, always.
          </p>
          {editions.length === 0 && (
            <p style={{ fontSize: 12.5, color: CREAM60, lineHeight: 1.6 }}>
              Nothing published yet covers this gap. That is a gap in our writing, not in you —
              and it is now on the editorial list.
            </p>
          )}
          {editions.map((e) => (
            <Card key={e.id} title={e.title} url={e.url}
                  meta={[e.published, e.readMinutes ? `${e.readMinutes} min read` : null]
                        .filter(Boolean).join(" · ")}
                  body={e.summary} badge="AI Signals" badgeColor={TEAL} closes={e.closes} />
          ))}
        </div>

        <div>
          <Label color={GREEN}>Ways to apply</Label>
          <p style={{ fontSize: 12, color: CREAM30, margin: "0 0 12px", lineHeight: 1.55 }}>
            Changes what you can <em>show</em>. This column matters more — a certificate nobody
            asked for is a credential whose price has already collapsed.
          </p>
          {modules.length === 0 && (
            <p style={{ fontSize: 12.5, color: CREAM60, lineHeight: 1.6 }}>
              No module covers this gap yet. Designing a way to prove a skill is harder than
              writing about it, which is exactly why this column is the one to watch.
            </p>
          )}
          {modules.map((m) => (
            <Card key={m.id} title={m.title} url={m.url}
                  meta={`${m.effort} · ${m.cost}`}
                  body={<><strong style={{ color: CREAM }}>You end up with:</strong> {m.evidence}</>}
                  badge="Do it" badgeColor={GREEN} closes={m.closes} />
          ))}
        </div>
      </div>

      {uncovered.length > 0 && (
        <p style={{ fontSize: 12, color: CREAM30, lineHeight: 1.6, margin: "22px 0 0", maxWidth: "62ch" }}>
          No route yet for {uncovered.map((s) => s.name).join(", ")}. Said plainly rather than
          hidden — a plan with a silent hole in it is worse than one that admits it.
        </p>
      )}

      <button onClick={onBack}
        style={{ background: "none", border: `1px solid ${CREAM30}`, color: CREAM60, marginTop: 24,
                 padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
        ← Back to pathways
      </button>
    </div>
  );
}
